import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import { customerService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import { buildCustomerActions, buildCustomerColumns, CustomerStatusChip, customerStatus } from './columns';
import { retryOnServerError } from '../../../components/dashboard/queryOptions';
import { formatTaxId } from '../../../utils/formatters';

// Only internal users ever receive soft-deleted customers from the API.
const STATUS_OPTIONS = ['active', 'inactive'];
const INTERNAL_STATUS_OPTIONS = [...STATUS_OPTIONS, 'deleted'];

const normalize = (value) => (value ?? '').toString().toLowerCase();
const digitsOnly = (value) => (value ?? '').toString().replace(/[^0-9a-z]/gi, '');

export default function CustomersList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const { collaborator } = useAuth();
  const isInternal = Boolean(collaborator?.is_internal);

  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  const customersQuery = useQuery({
    queryKey: ['customers'],
    retry: retryOnServerError,
    queryFn: () => customerService.getCustomers().then((res) => res.data),
  });

  const customers = useMemo(() => customersQuery.data ?? [], [customersQuery.data]);

  const providerOptions = useMemo(() => {
    const byUuid = new Map(customers.map((customer) => [customer.provider_uuid, customer.provider_name]));
    return [...byUuid.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  const matchesFilters = useCallback((customer, { statusValue, providerValue, searchValue }) => {
    const customerStatusValue = customerStatus(customer);
    // No status filter = every live customer; deleted ones only show up
    // when explicitly asked for (so they can be restored).
    if (statusValue === '' && customerStatusValue === 'deleted') return false;
    if (statusValue !== '' && customerStatusValue !== statusValue) return false;
    if (providerValue !== '' && customer.provider_uuid !== providerValue) return false;
    if (searchValue) {
      const term = normalize(searchValue);
      const taxTerm = digitsOnly(searchValue);
      const matches =
        normalize(customer.display_name).includes(term) ||
        normalize(customer.persona?.legal_name).includes(term) ||
        (taxTerm !== '' && digitsOnly(customer.persona?.tax_id).includes(taxTerm));
      if (!matches) return false;
    }
    return true;
  }, []);

  const filteredCustomers = useMemo(
    () =>
      customers.filter((customer) =>
        matchesFilters(customer, { statusValue: status, providerValue: provider, searchValue: search }),
      ),
    [customers, status, provider, search, matchesFilters],
  );

  const applySelection = useCallback(
    (overrides) => {
      const visibleIds = customers
        .filter((customer) =>
          matchesFilters(customer, {
            statusValue: overrides.statusValue ?? status,
            providerValue: overrides.providerValue ?? provider,
            searchValue: overrides.searchValue ?? search,
          }),
        )
        .map((customer) => customer.uuid);
      setSelectionModel((prev) => prev.filter((id) => visibleIds.includes(id)));
    },
    [customers, status, provider, search, matchesFilters],
  );

  const updateStatus = (value) => {
    setStatus(value);
    applySelection({ statusValue: value });
  };

  const updateProvider = (value) => {
    setProvider(value);
    applySelection({ providerValue: value });
  };

  const updateSearch = (value) => {
    setSearch(value);
    applySelection({ searchValue: value });
  };

  const clearFilters = () => {
    setStatus('');
    setProvider('');
    setSearch('');
    applySelection({ statusValue: '', providerValue: '', searchValue: '' });
  };

  const invalidateCustomers = () => queryClient.invalidateQueries({ queryKey: ['customers'] });

  const { pending, remove, undo, dismiss, seed } = useUndoDelete({
    onDelete: (uuid) => customerService.deleteCustomer(uuid).then(invalidateCustomers),
    onRestore: (uuid) => customerService.restoreCustomer(uuid).then(invalidateCustomers),
  });

  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleDeleteOne = (customer) => {
    remove(customer.uuid, t('customers.deleteSuccess'));
    setSelectionModel((prev) => prev.filter((id) => id !== customer.uuid));
  };

  const handleDeleteSelected = () => {
    const ids = [...selectionModel];
    remove(ids, t('customers.deleteSuccess'));
    setSelectionModel([]);
  };

  const handleRestore = async (customer) => {
    await customerService.restoreCustomer(customer.uuid);
    await invalidateCustomers();
    setFlashMessage(t('customers.restoreSuccess'));
  };

  const actionHandlers = {
    isInternal,
    onView: (customer) => navigate(`/customers/${customer.uuid}`),
    onEdit: (customer) => navigate(`/customers/${customer.uuid}/edit`),
    onDelete: handleDeleteOne,
    onRestore: handleRestore,
  };

  const columns = useMemo(
    () => buildCustomerColumns({ t, language: i18n.language, ...actionHandlers }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, navigate, isInternal],
  );

  const chips = [];
  if (status !== '') {
    chips.push({
      key: 'status',
      label: `${t('customers.filters.status')}: ${t(`customers.statuses.${status}`)}`,
      onDelete: () => updateStatus(''),
    });
  }
  if (provider !== '') {
    chips.push({
      key: 'provider',
      label: `${t('customers.filters.provider')}: ${providerOptions.find((option) => option.value === provider)?.label ?? ''}`,
      onDelete: () => updateProvider(''),
    });
  }

  const header = (
    <PageHeader
      title={t('customers.listTitle')}
      breadcrumbs={[
        { label: t('customers.breadcrumb.dashboard') },
        { label: t('nav.groups.admin') },
        { label: t('customers.breadcrumb.list') },
      ]}
      actions={
        isInternal && (
          <Button variant="contained" onClick={() => navigate('/customers/new')}>
            {t('customers.newCustomer')}
          </Button>
        )
      }
    />
  );

  if (customersQuery.isError) {
    const httpStatus = customersQuery.error?.response?.status;
    return (
      <BoundedPageLayout header={header}>
        {httpStatus === 403 ? (
          <PermissionDenied
            title={t('customers.states.permissionDeniedTitle')}
            message={t('customers.states.permissionDeniedMessage')}
          />
        ) : (
          <ErrorState
            title={t('customers.states.errorTitle')}
            message={t('customers.states.errorMessage')}
            actionLabel={t('customers.states.retry')}
            onRetry={customersQuery.refetch}
          />
        )}
      </BoundedPageLayout>
    );
  }

  const selectedCount = selectionModel.length;

  // Plain props object, NOT a function/component: `slots.toolbar` must stay a
  // stable reference (the imported ListFilterToolbar) across renders, or the
  // toolbar (and its focused search input) remounts on every keystroke.
  const toolbarProps = {
    filters: [
      {
        field: 'status',
        label: t('customers.filters.status'),
        value: status,
        onChange: updateStatus,
        options: (isInternal ? INTERNAL_STATUS_OPTIONS : STATUS_OPTIONS).map((value) => ({
          value,
          label: t(`customers.statuses.${value}`),
        })),
      },
      {
        field: 'provider',
        label: t('customers.filters.provider'),
        value: provider,
        onChange: updateProvider,
        options: providerOptions,
      },
    ],
    search: { label: t('customers.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('customers.filters.resultsFound', { count: filteredCustomers.length }),
    clearLabel: t('customers.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
    bulkAction: isInternal && selectedCount > 0 && (
      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteSelected}>
        {t('customers.list.deleteSelected', { count: selectedCount })}
      </Button>
    ),
  };

  return (
    <BoundedPageLayout header={header}>
      <DataGridCard>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {filteredCustomers.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {filteredCustomers
                  .slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize,
                  )
                  .map((customer) => (
                    <ListItem
                      key={customer.uuid}
                      divider
                      secondaryAction={
                        <RowActionsMenu actions={buildCustomerActions({ t, customer, ...actionHandlers })} />
                      }
                    >
                      {isInternal && !customer.is_deleted && (
                        <Checkbox
                          edge="start"
                          checked={selectionModel.includes(customer.uuid)}
                          onChange={(event) =>
                            setSelectionModel((prev) =>
                              event.target.checked
                                ? [...prev, customer.uuid]
                                : prev.filter((id) => id !== customer.uuid),
                            )
                          }
                          sx={{ mr: 1 }}
                        />
                      )}
                      <ListItemText
                        primary={customer.display_name}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              {formatTaxId(customer.persona?.tax_id, customer.persona?.country) || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('customers.list.businessesCount', { count: customer.businesses_count ?? 0 })}
                            </Typography>
                            <CustomerStatusChip t={t} customer={customer} />
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        ) : (
          <DataGrid
            // The "Cliente" column renders two lines (name + legal name); a
            // fixed density-based row height clips that second line in
            // compact mode, so let each row size to its own content.
            getRowHeight={() => 'auto'}
            rows={filteredCustomers}
            columns={columns}
            getRowId={(row) => row.uuid}
            loading={customersQuery.isLoading}
            checkboxSelection={isInternal}
            isRowSelectable={(params) => !params.row.is_deleted}
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
            }}
            localeText={{
              toolbarColumns: t('customers.filters.columns'),
              toolbarFilters: t('customers.filters.filters'),
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>

      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('customers.deleteUndo')} />

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('customers.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('customers.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
